// frontend/src/pages/public/SecurityPage.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  LockClosedIcon,
  EyeSlashIcon,
  KeyIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import Seo from "../../marketing/Seo";
import { Container, Section, Eyebrow, Badge } from "../../marketing/ui";
import { CTASection } from "../../marketing/blocks";

const PRACTICES = [
  {
    icon: EyeSlashIcon,
    title: "No raw IPs, ever",
    body: "Scan analytics are anonymised at the moment of capture — QRFlow never stores a raw IP address. You get the insight without the liability.",
  },
  {
    icon: LockClosedIcon,
    title: "Encrypted in transit",
    body: "All traffic is served over HTTPS/TLS. Credentials and sessions are protected with modern, industry-standard mechanisms.",
  },
  {
    icon: KeyIcon,
    title: "Strong authentication",
    body: "Short-lived access tokens with rotating refresh, CSRF protection, optional multi-factor authentication, and brute-force protection on login.",
  },
  {
    icon: UserGroupIcon,
    title: "Tenant isolation & roles",
    body: "Every organisation is an isolated tenant. Role-based access and scoped API keys ensure members only reach what they should.",
  },
  {
    icon: ServerStackIcon,
    title: "Data minimisation by design",
    body: "We collect only what the Service needs and prune detailed analytics on a per-plan retention schedule.",
  },
  {
    icon: ShieldCheckIcon,
    title: "GDPR-first",
    body: "Built around data-subject rights: export, rectification, and erasure that anonymises personal data while keeping aggregate statistics.",
  },
];

export default function SecurityPage() {
  return (
    <>
      <Seo
        title="Security"
        description="QRFlow's privacy-first security posture: no raw IPs, encryption in transit, MFA, tenant isolation, and GDPR by design."
        path="/security"
      />

      <div className="bg-gradient-to-b from-brand-50/60 to-white">
        <Container className="py-16 text-center sm:py-20">
          <Eyebrow>Security & privacy</Eyebrow>
          <h1 className="mx-auto mt-2 max-w-3xl text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Privacy-first by design
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-600">
            QRFlow is built so you get powerful analytics without collecting data you
            don't need — starting with never storing a raw IP address.
          </p>
        </Container>
      </div>

      <Section>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PRACTICES.map((p) => (
            <div key={p.title} className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <p.icon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">{p.title}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Compliance posture — honest about what's in place vs. on the roadmap */}
      <Section muted>
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">Compliance posture</h2>
          <p className="mt-4 text-gray-600">
            We design to GDPR principles today. Formal certifications are a roadmap item, and
            we'll publish them here as they're achieved — we won't claim a certification we
            don't hold.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Badge tone="accent">GDPR-aligned</Badge>
            <Badge tone="gray">SOC 2 — roadmap</Badge>
            <Badge tone="gray">ISO 27001 — roadmap</Badge>
          </div>
          <p className="mt-8 text-sm text-gray-600">
            Read how data is handled in our{" "}
            <Link to="/legal/privacy" className="font-medium text-brand-700 hover:text-brand-800">Privacy Policy</Link>{" "}
            and{" "}
            <Link to="/legal/dpa" className="font-medium text-brand-700 hover:text-brand-800">Data Processing Addendum</Link>.
          </p>
        </div>
      </Section>

      {/* Responsible disclosure */}
      <Section>
        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900">Reporting a vulnerability</h2>
          <p className="mt-3 text-gray-600">
            Found a security issue? We appreciate responsible disclosure. Email{" "}
            <a href="mailto:security@qrflow.ioanna.ro" className="font-medium text-brand-700 hover:text-brand-800">
              security@qrflow.ioanna.ro
            </a>{" "}
            with details and steps to reproduce, and give us reasonable time to respond before
            public disclosure.
          </p>
        </div>
      </Section>

      <CTASection
        title="Analytics you can trust"
        subtitle="Start free and see privacy-first QR analytics in action."
        primary={{ to: "/register", label: "Get started free" }}
        secondary={{ to: "/legal", label: "Read our policies" }}
      />
    </>
  );
}
